import { IVendor, IVendorExternalProperties, IVendorLight } from "../types/vendor";
import { ILoad } from "../types/load";

/**********
 * Determines the augmented state of a load for vendors with non-standard workflow requirements.
 * 
 * Currently supported vendors: Cirba, Redwood
 **********/
export class ExternalLoadStateManager {
	constructor(private readonly vendor: IVendor | IVendorLight, private readonly load: ILoad) {
		if (!vendor) throw new Error("ExternalLoadStateManager requires a vendor.");
		if (!load) throw new Error("ExternalLoadStateManager requires a load.");
	}

	private get vendorExtProps(): IVendorExternalProperties {
		return (this.vendor as IVendor)?.details.externalProperties || (this.vendor as IVendorLight).externalProperties;
	}

	/**********
	 * Indicates the active vendor profile.
	 **********/
	public get activeProfile(): string {
		if (this.redwood) return ExternalVendorProfile.Redwood;
		if (this.cirba) return ExternalVendorProfile.Cirba;
		return ExternalVendorProfile.Standard;
	}

	/**********
	 * Indicates whether the vendor follows the standard workflow.
	 **********/
	public get isStandard(): boolean {
		return !(this.vendorExtProps?.isRedwood || this.vendorExtProps?.isCirba);
	}

	/**********
	 * Indicates whether the load is currently in the specified status.
	 * @param status Name of the status to check.
	 **********/
	private isStatus = (statuses: LoadStatus[]): boolean => {
		return statuses && this.load && statuses.map(s => s.toString().toLowerCase()).includes(this.load?.status?.toLowerCase());
	}

	/**********
	 * Indicates whether the load has bypassed the external vendor's custom workflow.
	 **********/
	public get didBypassVendorWorkflow(): boolean {
		switch(this.activeProfile) {
			case ExternalVendorProfile.Redwood:
				return this.isStatus([LoadStatus.Scheduled, LoadStatus.Shipped]) && this.vendorExtProps?.isRedwood && !this.load.isRedwood;
			default:
				return false;
		}
	}

	/**********
	 * Provides state information for Cirba loads.
	 **********/
	public get cirba(): ICirbaLoadState | undefined {
		if (!this.vendorExtProps?.isCirba) return undefined;

		const state: ICirbaLoadState = {
			isReadyToSendWorkOrder: this.isStatus([LoadStatus.Ready]) && !this.load.loadDataSentToVendor,
			isWaitingToUploadBol: this.isStatus([LoadStatus.Ready]) && this.load.loadDataSentToVendor && !this.load.vendorLoadId,
			isWaitingToSchedulePickup: this.isStatus([LoadStatus.Ready]	) && !!this.load.loadDataSentToVendor && !!this.load.vendorLoadId
		};

		return state;
	}

	/**********
	 * Provides state information for Redwood loads.
	 **********/
	public get redwood(): IRedwoodLoadState | undefined {
		if (!this.vendorExtProps?.isRedwood) return undefined;

		const state: IRedwoodLoadState = {
			isReadyToSchedule: this.isStatus([LoadStatus.Ready]) && !this.load.loadDataSentToVendor,
			isAwaitingBol: this.isStatus([LoadStatus.Ready]) && !!this.load.loadDataSentToVendor
		};

		return state;
	}
}

/**********
 * Enumeration of load statuses.
 **********/
enum LoadStatus {
	/**********
	 * Load is being prepared.
	 **********/
	Loading = "Loading",

	/**********
	 * Load is closed/sealed and is ready to be scheduled for pickup.
	 **********/
	Ready = "Ready",

	/**********
	 * Load is scheduled for pickup and is waiting to be shipped.
	 **********/
	Scheduled = "Scheduled",

	/**********
	 * Load has been shipped.
	 **********/
	Shipped = "Shipped"
}

export enum ExternalVendorProfile {
	Standard = "Standard",
	Cirba = "Cirba",
	Redwood = "Redwood"
}

export interface IRedwoodLoadState {
	/*****
	 * State 1: Load is ready but has not been sent nor the load flagged as IsRedwood.
	 */
	isReadyToSchedule: boolean;

	/*****
	 * State 2: Load has been sent with a requested schedule date. Waiting to received a Redwood load ID to retrieve the BOL document.
	 */
	isAwaitingBol: boolean;

	// Status transitions to Scheduled after BOL is received so no further states are needed.
}

export interface ICirbaLoadState {
	/*****
	 * State 1: Load is ready but the Work Order PDF has not been populated and emailed to Cirba.
	 */
	isReadyToSendWorkOrder: boolean;
	/*****
	 * State 2: Work Order has been sent to Cirba. Waiting to receive a BOL document to upload to the load.
	 */
	isWaitingToUploadBol: boolean;
	/*****
	 * State 3: The BOL has been received from Cirba and uploaded. Waiting for pickup date to be scheduled.
	 */
	isWaitingToSchedulePickup: boolean;

	// Status transitions to Scheduled after scheduled pickup date is set so no further states are needed.
}